import React, { useCallback } from "react";
//#region redux
import { useDispatch } from "react-redux";
import { ThunkDispatch } from "redux-thunk";
import { addToDismissMessage } from "store/actions/utilActions";
//#endregion
//#region types
import { StyleEnums } from "models/enums";
import { MessageOptions, useFloatingMessagesOutput } from "models/propTypes/messagePropTypes";
//#endregion
//#region components
import { useSnackbar } from "notistack";
//#endregion

function useFloatingMessages(): useFloatingMessagesOutput {
  const dispatch: ThunkDispatch<any, any, any> = useDispatch();
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();

  const enqueueMessage = useCallback(
    (
      message: React.ReactNode,
      type: StyleEnums.DisplayType,
      autoHide?: boolean,
      multiline?: boolean,
      options?: MessageOptions
    ) => {
      let persist = true;
      let variant: "default" | "error" | "success" | "warning" | "info" = "default";

      switch (type) {
        case StyleEnums.DisplayType.Info:
          variant = "info";
          break;
        case StyleEnums.DisplayType.Success:
          variant = "success";
          persist = false;
          break;
        case StyleEnums.DisplayType.Warning:
          variant = "warning";
          break;
        case StyleEnums.DisplayType.Danger:
          variant = "error";
          break;
      }

      const key = enqueueSnackbar(message, {
        key: options?.key,
        persist: autoHide !== undefined ? !autoHide : persist,
        variant: variant,
        autoHideDuration: options?.autoHideDuration ?? 5000,
        preventDuplicate: options?.preventDuplicate ?? true,
        style: multiline ? { whiteSpace: "pre-wrap" } : undefined,
        className: options?.className,
      });

      const shouldDismissOnLocationChange =
        (type !== StyleEnums.DisplayType.Success || options?.addToDismissList) && !options?.excludeFromDismissList;
      if (shouldDismissOnLocationChange) dispatch(addToDismissMessage(key));

      return key;
    },
    []
  );

  const enqueueErrorMessage = (message: React.ReactNode, multiline?: boolean) =>
    enqueueMessage(message, StyleEnums.DisplayType.Danger, true, multiline);

  const enqueueSuccessMessage = (message: React.ReactNode, multiline?: boolean) =>
    enqueueMessage(message, StyleEnums.DisplayType.Success, true, multiline);

  return {
    enqueueMessage: enqueueMessage,
    closeMessage: closeSnackbar,
    enqueueErrorMessage,
    enqueueSuccessMessage,
  };
}

export default useFloatingMessages;
